import axios, { AxiosError } from 'axios';
import {
  ChiusuraCassaInfo_interface,
  ChiusuraCassaCalcoli_interface,
  ChiusuraCassaStampa_interface,
  IstitutiBancari_interface,
  ChiusuraCassa_ParametriRT_interface,
} from '../interface/ChiusuraCassa_interfaces';

// Base URL per le API
const API_BASE_URL = '/focusapi';

// Variabile globale per memorizzare l'ultimo errore
export let lastApiError: string | null = null;

// Funzione helper per gestire gli errori
const handleApiError = (error: AxiosError, operazione: string): never => {
  console.error(`Errore durante ${operazione}:`, error);
  let errorMessage: string;

  if (error.response) {
    // Errore proveniente dal server
    console.log('Response data:', error.response.data);
    errorMessage = error.response.data
      ? typeof error.response.data === 'string'
        ? error.response.data
        : JSON.stringify(error.response.data)
      : 'Errore sconosciuto';
  } else if (error.request) {
    // Nessuna risposta dal server
    errorMessage = 'Nessuna risposta dal server. Verifica la connessione.';
  } else {
    // Errore di configurazione della richiesta
    errorMessage = `Errore di configurazione: ${error.message}`;
  }

  // Memorizza l'ultimo errore per poterlo visualizzare nell'UI
  lastApiError = errorMessage;

  throw new Error(errorMessage);
};

/**
 * Recupera le informazioni base sulla chiusura cassa corrente
 * @returns Informazioni sulla chiusura cassa
 */
export const getChiusuraCassaInfo = async (
  dataChiusura: string,
): Promise<ChiusuraCassaInfo_interface> => {
  try {
    const response = await axios.get<ChiusuraCassaInfo_interface>(
      `${API_BASE_URL}/chiusuracassa/info?dataChiusura=${encodeURIComponent(
        dataChiusura,
      )}`,
    );
    return response.data;
  } catch (error) {
    return handleApiError(
      error as AxiosError,
      'il recupero delle informazioni di chiusura cassa',
    );
  }
};

/**
 * Calcola i totali della chiusura cassa in base ai parametri forniti
 * @param parametri Parametri per il calcolo della chiusura cassa
 * @returns Risultati del calcolo con dettagli dei pagamenti e IVA
 */
export const calcolaChiusuraCassa = async (
  parametri: ChiusuraCassa_ParametriRT_interface,
  codiceFiliale: string,
): Promise<ChiusuraCassaCalcoli_interface> => {
  try {
    const response = await axios.post<ChiusuraCassaCalcoli_interface>(
      `${API_BASE_URL}/chiusuracassa/calcoli`,
      { parametri: parametri, codiceFiliale: codiceFiliale },
    );
    return response.data;
  } catch (error) {
    return handleApiError(
      error as AxiosError,
      'il calcolo della chiusura cassa',
    );
  }
};

/**
 * Recupera la lista degli istituti bancari disponibili per il versamento
 * @returns Lista degli istituti bancari
 */
export const getListaIstitutiBancari = async (): Promise<
  IstitutiBancari_interface[]
> => {
  try {
    const response = await axios.get<IstitutiBancari_interface[]>(
      `${API_BASE_URL}/chiusuracassa/listabanche`,
    );
    return response.data;
  } catch (error) {
    return handleApiError(
      error as AxiosError,
      'il recupero della lista banche',
    );
  }
};

/**
 * Registra la chiusura cassa definitiva
 * @param parametri Parametri per la registrazione della chiusura cassa
 * @returns Risultato dell'operazione (true = successo)
 */
export const registraChiusuraCassa = async (
  parametri: ChiusuraCassaCalcoli_interface,
  codiceFiliale: string,
  parziale: boolean,
): Promise<boolean> => {
  try {
    const response = await axios.post<boolean>(
      `${API_BASE_URL}/chiusuracassa/registra`,
      { parametri: parametri, codiceFiliale: codiceFiliale, parziale },
    );
    return response.data;
  } catch (error) {
    // handleApiError lancerà un'eccezione, quindi questa riga non verrà mai eseguita
    // ma la gestiamo nel componente che chiama questa funzione
    return handleApiError(
      error as AxiosError,
      'la registrazione della chiusura cassa',
    );
  }
};

/**
 * Genera la stampa della chiusura cassa
 * @param parametri Parametri per la stampa (chiusura definitiva o controllo)
 * @returns Risultato dell'operazione (true = successo)
 */
export const stampaChiusuraCassa = async (
  parametri: ChiusuraCassaStampa_interface,
): Promise<boolean> => {
  try {
    const response = await axios.post<boolean>(
      `${API_BASE_URL}/chiusuracassa/stampa`,
      parametri,
    );
    return response.data;
  } catch (error) {
    return handleApiError(
      error as AxiosError,
      'la stampa della chiusura cassa',
    );
  }
};

/**
 * Recupera i pagamenti elettronici RFE per una determinata data
 * @param dataChiusura Data della chiusura cassa (formato ISO)
 * @returns Totale dei pagamenti elettronici
 */
export const getPagamentiElettroniciRFE = async (
  dataChiusura: string,
): Promise<number> => {
  try {
    const response = await axios.get<number>(
      `${API_BASE_URL}/chiusuracassa/pageletrfe?dataChiusura=${encodeURIComponent(
        dataChiusura,
      )}`,
    );
    return response.data;
  } catch (error) {
    return handleApiError(
      error as AxiosError,
      'il recupero dei pagamenti elettronici RFE',
    );
  }
};
